import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useBrandMode, MAX_COMPARE } from "./BrandModeContext";
import BrandLogo from "../common/BrandLogo";
import { AthleteMedia, schoolOrTeam } from "./DiscoverPage";
import {
  ARCHETYPE_DOT,
  ARCHETYPE_DOT_FALLBACK,
  EMPTY_VALUE,
  PLATFORMS,
  compareInsight,
  fmtCompact,
  fmtMoney,
  fmtRange,
  fullName,
} from "./brandUtils";
import { fitStrength, nationalRankLabel } from "./brandDealUtils";
import {
  Avatar,
  Badge,
  Btn,
  Card,
  InsightBox,
  Overline,
  PageHeader,
  SearchBox,
  SegmentedControl,
} from "./ui/BrandUI";

const narrative = (athlete) => {
  if (athlete.archetype === "Hidden Gem") {
    return "Elite on-field output with visibility yet to catch up, the best value window for an early apparel or nutrition partnership.";
  }
  if (athlete.archetype === "Visibility Star") {
    return "Visibility outpaces on-court output, a reach-led pitch for social-first campaigns.";
  }
  return "Reach and production both strong, the safest premium signing of this group and the strongest ambassador candidate.";
};

const readAcross = (athletes) => {
  const base = compareInsight(athletes);
  if (!base) return null;
  const premium = athletes.find((a) => a.archetype === "Complete Athlete" && (a.natlPct || 100) <= 10);
  return premium
    ? `${base} ${fullName(premium)} is the safest premium signing with reach and production both top-decile.`
    : base;
};

const Row = ({ label, value, muted = false }) => (
  <div className="flex items-center justify-between gap-3">
    <span className={`bm-body-m ${muted ? "text-bm-muted" : "text-bm-ink"}`}>{label}</span>
    <span className="bm-label-l text-bm-ink">{value}</span>
  </div>
);

const CompareCard = ({ athlete }) => {
  const { toggleCompare, shortlistHas, toggleShortlist } = useBrandMode();
  const platforms = athlete.followers?.platforms || {};
  const inShortlist = shortlistHas(athlete.athleteId);
  const rates = athlete.nilRates || {};
  const cv = athlete.cacheValue || {};
  const mid = cv.min && cv.max ? Math.round((cv.min + cv.max) / 2) : null;

  return (
    <div className="flex flex-col overflow-hidden rounded-[12px] border border-bm-border bg-white">
      <AthleteMedia athlete={athlete} tall>
        <button
          type="button"
          onClick={() => toggleCompare(athlete.athleteId)}
          aria-label={`Remove ${fullName(athlete)} from compare`}
          className="absolute right-4 top-[72px] flex h-7 w-7 items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60"
        >
          ×
        </button>
      </AthleteMedia>

      <div className="flex flex-1 flex-col gap-5 p-5">
        <Badge tone="outline" dot={ARCHETYPE_DOT[athlete.archetype] || ARCHETYPE_DOT_FALLBACK} className="w-fit">
          {athlete.archetype}
        </Badge>

        <div className="flex items-center gap-4 rounded-[8px] bg-bm-subtle p-4">
          <span className="bm-num-l text-bm-orange">{athlete.cacheScore}</span>
          <span className="flex flex-col gap-[2px]">
            <Overline>CacheScore</Overline>
            <span className="bm-label-l text-bm-ink">{nationalRankLabel(athlete.natlPct, athlete.natlBasis)}</span>
            <span className="bm-body-s text-bm-muted">
              {athlete.posPct && athlete.posPct < 100 ? `Pos Top ${athlete.posPct}% · ` : ""}
              {athlete.visTier} reach
            </span>
          </span>
        </div>

        <div>
          <Overline>CacheValue</Overline>
          {mid ? (
            <>
              <div className="mt-1 bm-num-l text-bm-ink">{fmtMoney(mid)}</div>
              <div className="mt-2 h-1 w-full rounded-full bg-bm-mutedbg">
                <div className="h-1 w-1/2 rounded-full bg-bm-orange" />
              </div>
              <div className="mt-2 flex justify-between bm-body-s text-bm-tertiary">
                <span>{fmtMoney(cv.min)} floor</span>
                <span>{fmtMoney(cv.max)} ceiling</span>
              </div>
            </>
          ) : (
            <div className="mt-1 bm-body-m text-bm-tertiary">No valuation yet</div>
          )}
        </div>

        <div className="rounded-[8px] bg-bm-subtle p-4">
          <Overline>Audience &amp; engagement</Overline>
          <div className="mt-2 flex items-center justify-between">
            <span className="bm-label-l text-bm-ink">{fmtCompact(athlete.followers?.total || 0)} total followers</span>
            <span className="bm-label-xs text-bm-positive">{athlete.engagement || 0}% overall</span>
          </div>
          <div className="mt-2 flex flex-col gap-[6px]">
            {PLATFORMS.filter((p) => platforms[p.key]).map((p) => (
              <div key={p.key} className="flex items-center justify-between bm-body-m">
                <span>
                  <span className="text-bm-ink">{p.label}</span>{" "}
                  <span className="text-bm-muted">{fmtCompact(platforms[p.key].followers)} followers</span>
                </span>
                <span className="text-bm-muted">{platforms[p.key].engagement || 0}%</span>
              </div>
            ))}
            {!Object.keys(platforms).length && <span className="bm-body-m text-bm-tertiary">No connected socials yet</span>}
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <Overline>NILx rate card range</Overline>
          {[
            { label: "Social media post", value: rates.socialPost },
            { label: "Reel / TikTok", value: rates.reel },
            { label: "Events", value: rates.events },
          ].map((row) => (
            <Row
              key={row.label}
              muted
              label={row.label}
              value={row.value ? `${fmtMoney(Math.round(row.value * 0.85))}–${fmtMoney(Math.round(row.value * 1.15))}` : EMPTY_VALUE}
            />
          ))}
        </div>

        {athlete.brandFit?.length ? (
          <div className="flex flex-col gap-2">
            <Overline>Partnership fit</Overline>
            <div className="grid grid-cols-3 gap-2">
              {athlete.brandFit.slice(0, 3).map((fit) => {
                const strength = fitStrength(fit.score);
                return (
                  <div key={fit.domain || fit.brand} className="flex flex-col items-center gap-1 rounded-[8px] border border-bm-border bg-bm-subtle px-2 py-3 text-center">
                    <BrandLogo domain={fit.domain} mono={(fit.brand || "?").slice(0, 2).toUpperCase()} size={32} />
                    <span className="mt-1 bm-label-l capitalize text-bm-ink">{fit.category || "Lifestyle"}</span>
                    <span className="truncate bm-label-l text-bm-orange">{fit.brand}</span>
                    <Badge tone="outline" dot={strength.dot}>{strength.label}</Badge>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

        <p className="bm-body-s text-bm-muted">{narrative(athlete)}</p>

        <div className="mt-auto">
          <Btn variant={inShortlist ? "tertiary" : "secondary"} className="w-full" onClick={() => toggleShortlist(athlete.athleteId)}>
            {inShortlist ? "✓ In shortlist" : "Add to shortlist"}
          </Btn>
        </div>
      </div>
    </div>
  );
};

const CompareTable = ({ athletes }) => (
  <Card padding="p-0" className="overflow-x-auto">
    <table className="w-full min-w-[900px] border-collapse">
      <thead>
        <tr>
          <th className="bm-th">Athlete</th>
          <th className="bm-th">Reach</th>
          <th className="bm-th !text-right">Score</th>
          <th className="bm-th">CacheValue</th>
          <th className="bm-th !text-right">Followers</th>
          <th className="bm-th !text-right">Engagement</th>
          <th className="bm-th !text-right">Nat'l · Pos</th>
          <th className="bm-th">Top brand fit</th>
        </tr>
      </thead>
      <tbody>
        {athletes.map((a) => (
          <tr key={a.athleteId} className="h-14">
            <td className="bm-td">
              <span className="flex items-center gap-[10px]">
                <Avatar name={a.name} surname={a.surname} src={a.avatar} size={32} />
                <span className="flex flex-col gap-[2px]">
                  <span className="bm-h-xs text-bm-ink">{fullName(a)}</span>
                  <span className="bm-body-s text-bm-muted">{[a.position, schoolOrTeam(a)].filter(Boolean).join(" · ")}</span>
                </span>
              </span>
            </td>
            <td className="bm-td"><Badge tone="outline">{a.visTier}</Badge></td>
            <td className="bm-td text-right bm-num-m">{a.cacheScore}</td>
            <td className="bm-td bm-body-m">{fmtRange(a.cacheValue) || EMPTY_VALUE}</td>
            <td className="bm-td text-right bm-body-m">{fmtCompact(a.followers?.total || 0)}</td>
            <td className="bm-td text-right bm-body-m">{a.engagement || 0}%</td>
            <td className="bm-td text-right bm-body-m" title={a.natlBasis || undefined}>
              {typeof a.natlPct === "number" ? `Top ${a.natlPct}%` : EMPTY_VALUE}
              {typeof a.posPct === "number" ? ` · ${a.posPct}% pos` : ""}
            </td>
            <td className="bm-td bm-body-m capitalize">{a.topBrandCategory || EMPTY_VALUE}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </Card>
);

const ComparePage = () => {
  const navigate = useNavigate();
  const { comparedAthletes, compareIds, toggleCompare, pool, addToShortlist, shortlistHas } = useBrandMode();
  const [view, setView] = useState("cards");
  const [search, setSearch] = useState("");

  const insight = useMemo(() => readAcross(comparedAthletes), [comparedAthletes]);

  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return pool
      .filter((a) => !compareIds.includes(a.athleteId))
      .filter((a) => `${a.name} ${a.surname} ${a.school} ${a.team || ""}`.toLowerCase().includes(q))
      .slice(0, 6);
  }, [search, pool, compareIds]);

  const addAll = () => {
    const fresh = comparedAthletes.filter((a) => !shortlistHas(a.athleteId));
    if (!fresh.length) return toast.info("Everyone here is already in your shortlist.");
    addToShortlist(fresh.map((a) => a.athleteId));
    return toast.success(`${fresh.length} athlete${fresh.length > 1 ? "s" : ""} added to shortlist.`);
  };

  return (
    <div>
      <PageHeader
        title="Compare"
        subtitle="Packaging read across the athletes you selected. Team context is display-only."
        actions={
          <>
            <Btn variant="secondary" onClick={() => navigate("/brand/discover")}>Back to Discover</Btn>
            <Btn onClick={addAll} disabled={!comparedAthletes.length}>Add all to shortlist</Btn>
          </>
        }
      />

      <div className="flex flex-col gap-4 pt-7">
        {!comparedAthletes.length ? (
          <Card className="py-16 text-center">
            <p className="bm-body-m text-bm-muted">No athletes selected yet.</p>
            <Btn className="mt-4" onClick={() => navigate("/brand/discover")}>Pick athletes in Discover</Btn>
          </Card>
        ) : (
          <>
            {insight && (
              <InsightBox title={`Read across ${comparedAthletes.length} athletes`} className="!px-6 !py-[22px]">
                {insight}
              </InsightBox>
            )}

            <div className="flex flex-wrap items-center gap-4">
              <SegmentedControl value={view} onChange={setView} options={["cards", "table"]} />
              <span className="bm-body-s text-bm-muted">
                {compareIds.length} of {MAX_COMPARE} selected · card view works best up to 4
              </span>
              <div className="relative ml-auto w-full sm:w-[260px]">
                <SearchBox aria-label="Add athlete to compare" value={search} onChange={setSearch} placeholder="Add athlete by name or school" />
                {searchResults.length > 0 && (
                  <div className="absolute left-0 top-11 z-20 w-full overflow-hidden rounded-[12px] border border-bm-border bg-white shadow-xl">
                    {searchResults.map((a) => (
                      <button
                        key={a.athleteId}
                        type="button"
                        onClick={() => {
                          toggleCompare(a.athleteId);
                          setSearch("");
                        }}
                        className="flex w-full items-center gap-3 px-4 py-2 text-left hover:bg-bm-subtle"
                      >
                        <Avatar name={a.name} surname={a.surname} src={a.avatar} size={28} />
                        <span className="bm-label-l text-bm-ink">{fullName(a)}</span>
                        <span className="ml-auto bm-body-s text-bm-tertiary">{schoolOrTeam(a)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {view === "cards" ? (
              <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                {comparedAthletes.map((athlete) => (
                  <CompareCard key={athlete.athleteId} athlete={athlete} />
                ))}
              </div>
            ) : (
              <CompareTable athletes={comparedAthletes} />
            )}
          </>
        )}

        <p className="bm-body-s text-bm-tertiary">
          Estimated ranges come from each athlete's NILx rate card. Team context is display only.
        </p>
      </div>
    </div>
  );
};

export default ComparePage;
